/**
  * @class meleDropdown
  * @description
  *
  * # Purpose
  * Implements `<mele-dropdown>` custom web element - a dropdown selector with
  * optional search, sorting, and keyboard navigation.
  *
  * # Usage
  *
  * ## Method 1: Direct items (no translation)
  * Use the `items` attribute with an array of [key, label] pairs.
  * Labels are displayed as-is without translation.
  *
  * ```js
  * parentEl.appendChildren({
  *   tag: 'mele-dropdown',
  *   items: [
  *     ['admin', 'Administrator'],
  *     ['user', 'Regular User'],
  *     ['guest', 'Guest Account'],
  *   ],
  *   selected: ['admin'],
  * })
  * ```
  *
  * ## Method 2: Prefix + translation
  * Use `prefix` and `label` template. Keys are fetched via getOptions(prefix),
  * and labels are constructed from template then translated.
  *
  * ```js
  * parentEl.appendChildren({
  *   tag: 'mele-dropdown',
  *   prefix: 'user.role.',
  *   label: '${prefix}${key}',  // e.g. 'user.role.admin' -> translated
  *   selected: ['admin'],
  * })
  * ```
  *
  * # Priority
  * If both `items` and `prefix` are set, `items` takes priority.
  *
  * # Optional features
  * - `searchbar: true` - enables search input in modal
  * - `sort: ['label+']` - sort by label ascending (use '-' for descending, '$' suffix for translated sort)
  * - `prompt: 'Select role'` - header text in modal
  *
  * # Events
  * - Publishes `mmListSelected` when selection changes
  * - Override `eventSelected` for custom handling
  * - Override `eventSearch` for custom async search
  */

import meleElement from 'meleElement'
const KEYS = { TAB: 9, ENTER: 13, ESC: 27, UP: 38, DOWN: 40 }

const PROPERTIES = _.extend({}, {
  prompt: { type: 'string', description: 'prompt displayed in header of modal selector' },
  prefix: { type: 'string', description: 'sets the prefix for all items' },
  selected: { type: 'array', description: 'list of keys of selected items' },

  searchbar: { type: 'boolean', description: 'whether to show search bar' },
  search: { type: 'string', description: 'search term; if set, list will display only items containing the term in at least one field defined in searchfields' },
  searching: { type: 'boolean', description: 'if true, displays search in progress indicator' },

  sort: { type: 'array', description: 'list of property+ or property- indicates sorting criteria; $ suffix after property name indicates sorting over translation of the property, @ suffix means numeric sorting' },

  open: { type: 'boolean', description: 'Indicates whether dropdown modal is open' },
  tabindex: { type: 'number', description: 'default html attribute which allows tab events on element' },

  label: { type: 'string', description: 'Sets the template for constructing label of items' },
  label2: { type: 'string', description: 'Sets the template for constructing label2 of items' },
  value: { type: 'string', description: 'Sets the template for constructing displayed value of items' },
  color: { type: 'string', description: 'Sets the template for constructing color class of items' },

  eventSelected: { type: 'function', description: 'method that can be overwritten to inform about change of selected items; default will publish mmListSelected' },
  eventSearch: { type: 'function', description: 'method that can be overwritten to provide custom search results in dropdowns' },

  items: { type: 'array', description: 'array of [key, value] pairs to display directly without translation' },

}, meleElement._properties)

const OPTION_ATTRS = ['label', 'label2', 'value', 'color']

let bodyTemplate
let iconTemplate

export default class meleDropdown extends meleElement {
  init() {
    if (!bodyTemplate) {
      bodyTemplate = this.createElementFast('mele-body')
      iconTemplate = this.ownerDocument.getElementById('icons').content.getElementById('arrow.s').cloneNode(true)
      iconTemplate.removeAttribute('id')
    }

    super.init()
    this._translations = {}
    this._optionsCache = {}
    this._sortRules = []
    this._voptions = []

    const fragment = this.ownerDocument.createDocumentFragment()
    this._body = fragment.appendChild(bodyTemplate.cloneNode())
    this._body.classList.add(this.classScope('body'))
    this._body.onclick = () => { this.open = true }
    const icon = iconTemplate.cloneNode(true)
    icon.onclick = () => { this.open = true }
    fragment.appendChild(icon)
    this.appendChild(fragment)
  }

  // Property change handlers

  openChanged(value) {
    this._preselected = 0
    if (value) {
      this._openModal()
      this._renderModalOptions()
    }
  }

  selectedChanged(value, old, interactive) {
    if (value.length > 0) {
      this._body.innerHTML = this._renderOption(this._getVoption(value[0]))
      this.eventSelected(this, value, old, interactive)
    } else {
      this._body.innerHTML = ''
    }
  }

  sortChanged(value) {
    this._sortRules = this._parseSortRules(value)
  }

  searchChanged(value) {
    value = _.toString(value)
    if (this._searchInput) this._searchInput.value = value

    if (typeof this.eventSearch === 'function') {
      this.searching = true
      this.eventSearch(this, value)
    } else {
      const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      this._filterItems(new RegExp(escaped, 'i'))
    }
  }

  itemsChanged() {
    if (this.open && this._elModal) {
      this._renderModalOptions()
    }
    if (this.selected?.length > 0) {
      this._body.innerHTML = this._renderOption(this._getVoption(this.selected[0]))
    }
  }

  labelChanged(value) { if (value) this._updateBodyOption('label') }
  label2Changed(value) { if (value) this._updateBodyOption('label2') }
  colorChanged(value) { if (value) this._updateBodyOption('color') }
  valueChanged(value) { if (value) this._updateBodyOption('value') }
  prefixChanged() {}
  tabindexChanged() {}
  searchbarChanged() {}

  // Events

  eventSelected(_, items, old, interactive) {
    this.publish('mmListSelected', {
      holder: this.key,
      selected: items.join('∫'),
      old: old.join('∫'),
      interactive: !!interactive,
    })
  }

  onfocus() {}

  onkeydown(e) {
    this._handleKeyboard(e)
  }

  languageChanged() {
    this._translations = {}
    if (this.selected?.[0]) {
      this._body.innerHTML = this._renderOption(this._getVoption(this.selected[0]))
    }
    super.languageChanged()
  }

  // Private methods

  _getOptionsVdom() {
    // Direct items: [[key, value], ...] or [value, ...] - no translation
    if (this.items?.length > 0) {
      const result = []
      for (const item of this.items) {
        if (Array.isArray(item)) {
          result.push({ key: _.toString(item[0]), label: item[1], _direct: true })
        } else {
          const value = _.toString(item)
          result.push({ key: value, label: value, _direct: true })
        }
      }
      return result
    }

    if (!this.prefix) return null

    if (!this._optionsCache[this.prefix]) {
      this._optionsCache[this.prefix] = this.getOptions(this.prefix)
    }

    return this._optionsCache[this.prefix].map(key => this._getVoption(key))
  }

  _getVoption(key) {
    // Check direct items first
    if (this.items?.length > 0) {
      const found = this.items.find((item) => {
        const itemKey = Array.isArray(item) ? item[0] : item
        return _.toString(itemKey) === _.toString(key)
      })
      if (found) {
        if (Array.isArray(found)) {
          return { key: _.toString(found[0]), label: found[1], _direct: true }
        }
        const value = _.toString(found)
        return { key: value, label: value, _direct: true }
      }
    }

    // Check cached voptions
    const existing = _.find(this._voptions, { key })
    if (existing) return existing

    // Create new voption from templates
    const voption = { key: _.toString(key) }
    for (const attr of OPTION_ATTRS) {
      if (this[attr]) voption[attr] = this[attr]
    }
    this._voptions.push(voption)
    return voption
  }

  _renderOption(voption) {
    if (!_.isObjectLike(voption) || !voption.key) return ''

    const { key, _direct, ...attrs } = voption
    const isSelected = this.selected?.[0] === key ? ' selected' : ''
    let html = `<dropdown-item${isSelected} class="${this.classScope('item')}" key="${key}">`

    for (const [attr, template] of Object.entries(attrs)) {
      const value = _direct ? template : this._translateTemplate(template, key)
      if (attr === 'color') {
        html += `<dropdown-color class="${value} ${this.classScope('color')}"></dropdown-color>`
      } else {
        html += `<dropdown-${attr} class="${this.classScope(attr)}">${value}</dropdown-${attr}>`
      }
    }

    return html + '</dropdown-item>'
  }

  _translateTemplate(template, key) {
    const result = template
      .replace(/\${prefix}/g, this.prefix || '')
      .replace(/\${key}/g, key)

    if (!this._translations[result]) {
      this._translations[result] = this.translate(result)
    }
    return this._translations[result]
  }

  _renderModalOptions() {
    let options = this._getOptionsVdom()
    if (!options) return

    if (this._sortRules.length > 0) {
      options = this._sortOptions(options)
    }

    this._elModal._body.innerHTML = options.map(opt => this._renderOption(opt)).join('')
    this._items = Array.from(this._elModal._body.children)
  }

  _updateBodyOption(attr) {
    const selected = this.selected?.[0]
    const item = this._body.firstElementChild
    if (item && !item.hasAttribute(attr) && selected) {
      this._body.innerHTML = this._renderOption(this._getVoption(selected))
    }
  }

  // Sorting

  _parseSortRules(attrs) {
    return attrs.map(pred => {
      const match = pred.match(/^(\w+(?:\.\w+)*)([$#]?)([+-])$/)
      if (!match) {
        console.error('Invalid sort predicate:', pred)
        return null
      }
      const [, prop, format, order] = match
      return {
        prop,
        format,
        order: order === '-' ? -1 : 1,
        getValue: (obj) => {
          return format === '$' ? this._translateTemplate(obj[prop], obj.key) : obj[prop]
        },
      }
    }).filter(Boolean)
  }

  _sortOptions(array) {
    const rules = this._sortRules
    return [...array].sort((a, b) => {
      for (const rule of rules) {
        const valA = rule.getValue(a)
        const valB = rule.getValue(b)
        if (valA === valB) continue
        return (valA < valB ? -1 : 1) * rule.order
      }
      return 0
    })
  }

  // Search

  _filterItems(pattern) {
    this._items.forEach(item => {
      const text = Array.from(item.children).map(el => el.innerHTML).join(' ')
      if (pattern.test(text)) {
        item.removeAttribute('hidden')
      } else {
        item.setAttribute('hidden', '')
      }
    })
  }

  // Keyboard navigation

  _handleKeyboard(e) {
    if (!this._items) return

    const parentWindow = this.closest('mele-window')
    if (e.type === 'focus') {
      if (parentWindow && !parentWindow.focused) return
      this.open = true
      const visible = this._items.filter(item => !item.hasAttribute('hidden'))
      if (visible[0]) visible[0].classList.add('active')
      return
    }

    const key = e.keyCode || e.which

    if (![KEYS.TAB, KEYS.ENTER, KEYS.ESC, KEYS.UP, KEYS.DOWN].includes(key)) return

    const active = this._items.find(i => i.classList.contains('active'))
    const visible = this._items.filter(item => !item.hasAttribute('hidden'))

    if (key === KEYS.TAB || key === KEYS.ENTER || key === KEYS.ESC) {
      if (key === KEYS.ENTER && active) {
        this.setProperty('selected', [active.getAttribute('key')], true)
      }
      this.open = false
      this.modalClose(':dropdown')
    } else {
      const len = visible.length
      if (key === KEYS.UP) {
        this._preselected = (this._preselected + len - 1) % len
      } else {
        this._preselected = (this._preselected + 1) % len
      }
      if (active) active.classList.remove('active')
      visible[this._preselected]?.classList.add('active')
    }
  }

  // Modal

  _openModal() {
    this._elModal = this.modalOpen({
      key: ':dropdown',
      position: this.searchbar ? 'over' : 'below',
      tabindex: this.tabindex,
      header: this.prompt,
      headerbuttons: ['modalx'],
      class: 'dropdown',
      onclick: (e) => {
        const item = e.target.closest('dropdown-item')
        this.modalClose(':dropdown')
        this.open = false
        if (item) {
          this.setProperty('selected', [item.getAttribute('key')], true)
        }
      },
      eventClose: () => {
        this.open = false
      },
    })

    if (this.searchbar) {
      this._searchInput = this._elModal._header_wrapper.appendChildren({
        tag: 'mele-input',
        type: 'string',
        value: '',
        split: '0:1',
        eventValueChanged: (_, value) => {
          if (this.search !== value) this.search = value
        },
        onkeydown: (e) => {
          this._handleKeyboard(e)
        },
      })
      this._searchInput._elValueElement?.focus()
    }
  }
}

meleDropdown._properties = PROPERTIES
meleDropdown.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-dropdown', meleDropdown)
